import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentSlug = slug || 'all';

  const [activeTab, setActiveTab] = useState(currentSlug);

  useEffect(() => {
    setActiveTab(currentSlug);
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];

  const filteredVideos = useMemo(() => {
    if (currentSlug === 'all') return videos;
    return videos.filter((v) => v.category === currentSlug);
  }, [currentSlug]);

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('breadcrumb.category') },
  ];

  const handleTabChange = (catSlug: string) => {
    setActiveTab(catSlug);
    navigate(`/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `91视频网站有哪些${currentCategory.name}视频资源？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `91视频网站提供最新最全的高清${currentCategory.name}视频内容，用户可免费在线观看，享受流畅的观影体验。`,
        },
      },
      {
        '@type': 'Question',
        name: '如何在91视频网站上找到更多高清视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在91视频网站浏览首页精选推荐、按分类筛选视频，或使用搜索功能查找您喜欢的视频内容。所有视频均可免费观看。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-100 min-h-[60vh]">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500">
                {t('category.resultCount', { count: filteredVideos.length })}
              </p>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} size="medium" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-400">
                <i className="ri-video-line text-5xl mb-4"></i>
                <p>{t('category.noVideos')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}